// src/pages/finance-portal/new-hire-reports/NewHireReports.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, Skeleton, CircularProgress,
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NewHireReports() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [hires, setHires] = useState([]);
    const [summary, setSummary] = useState({ total: 0, overdue: 0, pending: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);

    // Date range filter
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/payroll/compliance/new-hire-status');
            if (res.data.success) {
                const data = res.data.data || {};
                const employees = data.employees || [];
                setHires(employees);

                const overdue = employees.filter(e => e.status === 'OVERDUE').length;
                const pending = employees.filter(e => e.status === 'PENDING').length;
                setSummary({
                    total: employees.length,
                    overdue,
                    pending,
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load new hire status');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    const handleGenerateReport = async () => {
        if (!startDate || !endDate) {
            enqueueSnackbar('Please select both start and end dates', { variant: 'warning' });
            return;
        }
        setGenerating(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/compliance/new-hire-report', {
                start_date: startDate,
                end_date: endDate,
            });
            if (res.data.success) {
                enqueueSnackbar('New hire report generated successfully', { variant: 'success' });
                fetchStatus();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate report';
            setError(msg);
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    const summaryCards = [
        { label: 'Total New Hires', value: summary.total, icon: <PeopleIcon />, bg: '#eff6ff', color: '#1d4ed8' },
        { label: 'Overdue', value: summary.overdue, icon: <WarningAmberIcon />, bg: '#fef2f2', color: '#dc2626' },
        { label: 'Pending', value: summary.pending, icon: <PendingActionsIcon />, bg: '#fffbeb', color: '#d97706' },
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <AssignmentIndIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 32 }} />
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.75rem', color: '#fff' }}>
                            New Hire Reports
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                        Track and generate state new hire reporting compliance
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {summaryCards.map((card) => (
                        <Grid item xs={12} sm={4} key={card.label}>
                            <Card elevation={0} sx={{
                                p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0',
                                display: 'flex', alignItems: 'center', gap: 2,
                            }}>
                                <Box sx={{
                                    width: 48, height: 48, borderRadius: '12px',
                                    bgcolor: card.bg, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: card.color,
                                }}>
                                    {card.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: card.color }}>
                                        {isLoading ? <Skeleton width={40} /> : card.value}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Date Range Filter & Generate */}
                <Paper elevation={0} sx={{
                    p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3,
                }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', mb: 2, color: '#1e293b' }}>
                        Generate New Hire Report
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...inputSx, minWidth: 180 }}
                            size="small"
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...inputSx, minWidth: 180 }}
                            size="small"
                        />
                        <Button
                            variant="contained"
                            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                            onClick={handleGenerateReport}
                            disabled={generating}
                            sx={{
                                fontFamily, fontWeight: 700, borderRadius: '10px',
                                bgcolor: ACCENT, textTransform: 'none', px: 3,
                                '&:hover': { bgcolor: '#16a34a' },
                            }}
                        >
                            {generating ? 'Generating...' : 'Generate Report'}
                        </Button>
                    </Box>
                </Paper>

                {/* Table */}
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Employee Name</TableCell>
                                    <TableCell sx={headCellSx}>Hire Date</TableCell>
                                    <TableCell sx={headCellSx}>Work State</TableCell>
                                    <TableCell sx={headCellSx}>Report Deadline</TableCell>
                                    <TableCell sx={headCellSx}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(5)].map((_, j) => (
                                                <TableCell key={j} sx={bodyCellSx}>
                                                    <Skeleton />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : hires.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ ...bodyCellSx, textAlign: 'center', py: 4 }}>
                                            No new hire records found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    hires.map((hire, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>
                                                {hire.employee_name || `${hire.first_name || ''} ${hire.last_name || ''}`.trim() || '—'}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(hire.hire_date)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{hire.work_state || '—'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatDate(hire.report_deadline)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip
                                                    label={hire.status || 'Unknown'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: hire.status === 'OVERDUE' ? '#fecaca'
                                                            : hire.status === 'PENDING' ? '#fef3c7'
                                                            : '#dcfce7',
                                                        color: hire.status === 'OVERDUE' ? '#991b1b'
                                                            : hire.status === 'PENDING' ? '#92400e'
                                                            : '#166534',
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
